import { Component, OnInit } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { UniversalSearchService } from 'src/app/services/universal-search.service';

@Component({
  selector: 'app-universal-search',
  templateUrl: './universal-search.component.html',
  styleUrls: ['./universal-search.component.scss']
})
export class UniversalSearchComponent implements OnInit {
  searchTerm: string = '';
  showResults: boolean = false;
  searchResults: any[] = [];
  categorizedResults: any = {
    products: [],
    doctors: [],
    hospitals: [],
    clinics: [],
    laboratories: [],
    webinars: [],
    jobs: [],
    categories: [],
    brands: [],
    distributors: []
  };
  currentPlaceholder: string = 'Products';
  private placeholderIndex: number = 0;
  private placeholderInterval: any;
  debouncerTimer: any;

  constructor(
    private router: Router,
    private universalSearchService: UniversalSearchService
  ) {}

  ngOnInit(): void {
    this.startPlaceholderRotation();
  }

  onSearchDebounced() {
    clearTimeout(this.debouncerTimer);
    this.debouncerTimer = setTimeout(() => {
      this.onSearch();
    }, 450);
  }

  onSearch(): void {
    if (this.searchTerm.length <= 2 || this.searchTerm==="") {
      this.showResults = false;
      return;
    }

    this.universalSearchService.search(this.searchTerm).subscribe({
      next: (response) => {
        this.searchResults = response.data || [];
        this.categorizeResults();
        this.showResults = true;
      },
      error: (error) => {
        this.showResults = false;
      }
    });
  }

  placeholderOptions: string[] = [
    'Products',
    'Brands',
    'Webinars',
    'Doctors',
    'Hospitals',
    'Clinics',
    'Labs',
    'Distributors',
    'Jobs',
    'Categories'
  ];

  startPlaceholderRotation(): void {
    this.placeholderInterval = setInterval(() => {
      this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderOptions.length;
      this.currentPlaceholder = this.placeholderOptions[this.placeholderIndex];
    }, 2500);
  }

  categorizeResults(): void {
    this.categorizedResults = {
      products: [],
      doctors: [],
      hospitals: [],
      clinics: [],
      laboratories: [],
      webinars: [],
      jobs: [],
      categories: [],
      brands: [],
      distributors:[]
    };

    this.searchResults.forEach(item => {
      switch (item.type) {
        case 'Product':
          this.categorizedResults.products = this.categorizedResults.products.concat(item).slice(0, 10);
          break;
        case 'Doctor':
          this.categorizedResults.doctors = this.categorizedResults.doctors.concat(item).slice(0, 5);
          break;
        case 'Hospital':
          this.categorizedResults.hospitals = this.categorizedResults.hospitals.concat(item).slice(0, 5);
          break;
        case 'Clinics':
          this.categorizedResults.clinics = this.categorizedResults.clinics.concat(item).slice(0, 5);
          break;
        case 'Laboratory':
          this.categorizedResults.laboratories = this.categorizedResults.laboratories.concat(item).slice(0, 5);
          break;
        case 'Webinar':
          this.categorizedResults.webinars = this.categorizedResults.webinars.concat(item).slice(0, 10);
          break;
        case 'jobPost':
          this.categorizedResults.jobs = this.categorizedResults.jobs.concat(item).slice(0, 10);
          break;
        case 'Category':
          this.categorizedResults.categories = this.categorizedResults.categories.concat(item).slice(0, 10);
          break;
        case 'OEM':
          this.categorizedResults.brands = this.categorizedResults.brands.concat(item).slice(0, 16);
          break;
        case 'Distributor':
          this.categorizedResults.distributors = this.categorizedResults.distributors.concat(item).slice(0, 16);
          break;
      }
    });
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.showResults = false;
  }

  navigateToItem(item: any, type: string): void {
    let route: string | null = null;
    const queryParams: any = { searchKeyword: this.searchTerm };

    switch (type.toLowerCase()) {
      case 'product':
        route = `/product-details/${item.id}`;
        break;
      case 'doctor':
        route = `/doctor-info/${item.id}`;
        break;
      case 'hospital':
        route = `/hospitalDetails/${item.id}`;
        break;
      case 'clinics':
        route = `/clinics/${item.id}`;
        break;
      case 'laboratory':
        route = `/labs/${item.id}`;
        break;
      case 'webinar':
        route = `/webinars/view/${item.id}`;
        break;
      case 'job':
        route = `/jobs/job-details/${item.id}`;
        break;
      case 'category':
        route = `/category/${item.id}`;
        break;
      case 'brand':
        this.navigateToBrandProfile(item.id);
        return;
      case 'distributor':
        this.navigateToDistributorProfile(item.id);
        return;
      default:
        return;
    }

    if (route) {
      this.router.navigate([route], { queryParams });
      this.clearSearch();
    }
  }

  navigateToBrandProfile(oemId: string): void {
    const navigationExtras: NavigationExtras = {
      queryParams: { type: 'brand', searchKeyword: this.searchTerm }
    };
    this.router.navigate([`/brands/brand-profile/${oemId}`], navigationExtras);
    this.clearSearch();
  }
  navigateToDistributorProfile(distributorId: string): void {
    const navigationExtras: NavigationExtras = {
      queryParams: { type: 'distributor', searchKeyword: this.searchTerm }
    };
    this.router.navigate([`/brands/brand-profile/${distributorId}`], navigationExtras);
    this.clearSearch();
  }
  searchInSection(section: string): void {
    let route: string;
    const sectionsWithResults = this.getSectionsWithResults();
    if (!sectionsWithResults.includes(section)) return;

    switch (section) {
      case 'products':
        route = `/products-search`;
        break;
      case 'doctors':
        route = `/doctors`;
        break;
      case 'hospitals':
        route = `/hospitals`;
        break;
      case 'clinics':
        route = `/clinics`;
        break;
      case 'labs':
        route = `/labs`;
        break;
      case 'jobs':
        route = `/jobs/view-all-jobs`;
        break;
      case 'brands':
        route = `/brands`;
        break;
      case 'distributors':
        route = `/website-distributor`;
        break;
      default:
        return;
    }
    if(route !== '/products-search') {
      this.router.navigate([route], { queryParams: { search: this.searchTerm } });
    } else if(route === '/products-search'){
      this.router.navigate([route], { queryParams: { search: this.searchTerm } });
    }
    this.clearSearch(); 
  }

  getSectionsWithResults(): string[] {
    const sections = [];
    if (this.categorizedResults.products.length) sections.push('products');
    if (this.categorizedResults.doctors.length) sections.push('doctors');
    if (this.categorizedResults.hospitals.length) sections.push('hospitals');
    if (this.categorizedResults.clinics.length) sections.push('clinics');
    if (this.categorizedResults.laboratories.length) sections.push('labs');
    if (this.categorizedResults.webinars.length) sections.push('webinars');
    if (this.categorizedResults.jobs.length) sections.push('jobs');
    if (this.categorizedResults.brands.length) sections.push('brands');
    if (this.categorizedResults.distributors.length) sections.push('distributors');
    if (this.categorizedResults.categories.length) sections.push('categories');
    return sections;
  }
  private hasItems(keys: string[]): boolean {
    return keys.some(key => this.categorizedResults[key]?.length > 0);
  }
  
  hasHealthcareKnowledgeItems(): boolean {
    return this.hasItems(['webinars', 'jobs']);
  }
  
  hasHealthcareNetworkItems(): boolean {
    return this.hasItems(['doctors', 'hospitals', 'clinics', 'laboratories']);
  }
  
  hasOtherItems(): boolean {
    return this.hasItems([
      'products', 'brands', 'doctors', 'hospitals', 'clinics', 'laboratories', 'jobs'
    ]);
  }
  
  noData(): boolean {
    return this.hasItems([
      'products', 'brands', 'categories', 'doctors', 'hospitals',
      'clinics', 'laboratories', 'webinars', 'jobs','distributors'
    ]);
  }
  
}